import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Alert, Slider, Divider,
  Chip, useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DownloadIcon from '@mui/icons-material/Download';
import MapView from './MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { readFileAsText, parseAnyGeoFile, haversineDistance, calculateRouteDeviation } from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';

const extractCoordinatesFromGeoJSON = (geoJson) => {
  if (!geoJson) return [];
  const features = geoJson.features || [geoJson];
  for (const f of features) {
    const geom = f.geometry || f;
    if (geom.type === 'LineString') return geom.coordinates;
    if (geom.type === 'MultiLineString') return geom.coordinates.flat();
    if (geom.type === 'Point') return [geom.coordinates];
  }
  // Try FeatureCollection of Points
  if (geoJson.features) {
    const pts = geoJson.features
      .filter(f => f.geometry?.type === 'Point')
      .map(f => f.geometry.coordinates);
    if (pts.length > 0) return pts;
  }
  return [];
};

function RouteMatcherVisualizer() {
  const theme = useTheme();
  const [rawGeoJson, setRawGeoJson] = useState(null);
  const [matchedGeoJson, setMatchedGeoJson] = useState(null);
  const [rawCoords, setRawCoords] = useState([]);
  const [matchedCoords, setMatchedCoords] = useState([]);
  const [error, setError] = useState('');
  const [deviationThreshold, setDeviationThreshold] = useState(50);

  const handleUpload = useCallback(async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'No coordinates found');
      const coords = extractCoordinatesFromGeoJSON(result.geoJson);
      if (coords.length === 0) throw new Error('No coordinates found in parsed data');

      if (type === 'raw') {
        setRawGeoJson(result.geoJson);
        setRawCoords(coords);
      } else {
        setMatchedGeoJson(result.geoJson);
        setMatchedCoords(coords);
      }
      setError('');
    } catch (err) {
      setError(`Failed to load ${type} route: ${err.message}`);
    }
  }, []);

  const deviation = useMemo(() => {
    if (rawCoords.length === 0 || matchedCoords.length === 0) return null;
    return calculateRouteDeviation(rawCoords, matchedCoords);
  }, [rawCoords, matchedCoords]);

  // Build a combined GeoJSON for visualization
  const combinedGeoJson = useMemo(() => {
    if (!deviation) return null;
    const features = [];

    // Matched route as solid blue
    if (matchedCoords.length > 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: matchedCoords },
        properties: { layer: 'matched', name: 'Matched Route' },
      });
    }

    // Raw route segments colored by deviation
    for (let i = 1; i < rawCoords.length; i++) {
      const dev = deviation.deviations[i]?.deviation || 0;
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [rawCoords[i - 1], rawCoords[i]] },
        properties: { layer: 'raw', deviation: dev, index: i },
      });
    }

    // Deviation lines connecting raw to matched points (for high deviation)
    deviation.deviations.forEach((d, i) => {
      if (d.deviation > deviationThreshold) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [d.rawPoint, d.matchedPoint] },
          properties: { layer: 'connector', deviation: d.deviation },
        });
      }
    });

    return { type: 'FeatureCollection', features };
  }, [deviation, rawCoords, matchedCoords, deviationThreshold]);

  const styleFunction = useCallback((feature) => {
    const props = feature.properties;
    if (props.layer === 'matched') {
      return { color: '#3b82f6', weight: 4, opacity: 0.9 };
    }
    if (props.layer === 'connector') {
      return { color: '#fbbf24', weight: 1, opacity: 0.5, dashArray: '4 4' };
    }
    // Raw route: color by deviation
    const dev = props.deviation || 0;
    const ratio = Math.min(dev / Math.max(deviationThreshold * 2, 1), 1);
    const r = Math.round(255 * ratio);
    const g = Math.round(255 * (1 - ratio));
    return { color: `rgb(${r}, ${g}, 80)`, weight: 3, opacity: 0.85 };
  }, [deviationThreshold]);

  const handleExportReport = useCallback(() => {
    if (!deviation) return;
    const report = {
      summary: deviation.stats,
      deviationThreshold,
      highDeviationPoints: deviation.deviations
        .filter(d => d.deviation > deviationThreshold)
        .map((d, i) => ({ index: i, deviation: d.deviation, raw: d.rawPoint, matched: d.matchedPoint })),
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'route_deviation_report.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [deviation, deviationThreshold]);

  const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(1)} m`;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Route Matcher Visualizer | Compare GPS Trajectories | UmbrellaCo"
        description="Compare raw GPS traces against matched routes to calculate deviation. Free online spatial visualization tool for telematics and GIS data."
        keywords="route matcher, gps deviation, compare gps traces, spatial deviation calculator"
        url="/spatial-tools/route-matcher"
        jsonLd={createSoftwareAppSchema(
          'Route Matcher Visualizer',
          'Compare raw GPS traces against matched routes to calculate deviation.',
          'https://umbrellaco.in/spatial-tools/route-matcher',
          ['Route Deviation Analysis', 'GPS Trace Comparison', 'Map Visualization']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Route Matcher Visualizer</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a raw GPS trace and a map-matched route to visualize route deviation, compare distances, and export variance reports.
        </Typography>
      </Box>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={32} minSize={22}>
          <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto',
            bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}`,
          }}>
            {/* Upload Section */}
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Load Routes</Typography>
              <Stack spacing={1.5}>
                <Button
                  component="label" variant="outlined" fullWidth size="small"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    borderColor: rawCoords.length > 0 ? '#ef4444' : undefined,
                    color: rawCoords.length > 0 ? '#ef4444' : undefined,
                  }}
                >
                  {rawCoords.length > 0 ? `Raw Route (${rawCoords.length} pts)` : 'Upload Raw Route'}
                  <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={(e) => handleUpload(e, 'raw')} />
                </Button>
                <Button
                  component="label" variant="outlined" fullWidth size="small"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    borderColor: matchedCoords.length > 0 ? '#3b82f6' : undefined,
                    color: matchedCoords.length > 0 ? '#3b82f6' : undefined,
                  }}
                >
                  {matchedCoords.length > 0 ? `Matched Route (${matchedCoords.length} pts)` : 'Upload Matched Route'}
                  <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={(e) => handleUpload(e, 'matched')} />
                </Button>
              </Stack>

              {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

              <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                <Chip size="small" sx={{ bgcolor: '#ef444422', color: '#ef4444', fontWeight: 600 }} label="━ Raw" />
                <Chip size="small" sx={{ bgcolor: '#3b82f622', color: '#3b82f6', fontWeight: 600 }} label="━ Matched" />
              </Stack>
            </Box>

            <Divider />

            {/* Deviation Settings */}
            {deviation && (
              <>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Deviation Threshold</Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Slider
                      value={deviationThreshold}
                      onChange={(e, v) => setDeviationThreshold(v)}
                      min={5} max={500} step={5}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 50 }}>{deviationThreshold}m</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Points deviating more than this are flagged
                  </Typography>
                </Box>

                <Divider />

                {/* Statistics */}
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                    <CompareArrowsIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>Comparison Stats</Typography>
                  </Stack>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {[
                      { label: 'Raw Distance', value: fmtDist(deviation.stats.totalRawDistance) },
                      { label: 'Matched Distance', value: fmtDist(deviation.stats.totalMatchedDistance) },
                      { label: 'Max Deviation', value: fmtDist(deviation.stats.maxDeviation) },
                      { label: 'Avg Deviation', value: fmtDist(deviation.stats.avgDeviation) },
                      { label: 'Median Deviation', value: fmtDist(deviation.stats.medianDeviation) },
                      { label: 'Flagged Points', value: deviation.deviations.filter(d => d.deviation > deviationThreshold).length },
                    ].map(({ label, value }) => (
                      <Paper key={label} variant="outlined" sx={{
                        p: 1, textAlign: 'center',
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(59,130,246,0.06)'
                          : 'rgba(59,130,246,0.03)',
                      }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={700}>{value}</Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>

                <Divider />

                {/* Export */}
                <Box sx={{ p: 2 }}>
                  <Button
                    variant="outlined" fullWidth size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportReport}
                  >
                    Export Deviation Report
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Panel>

        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

        <Panel defaultSize={68} minSize={40}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {combinedGeoJson ? (
              <MapView
                mapId="route-matcher-map"
                geoJsonData={combinedGeoJson}
                styleFunction={styleFunction}
              />
            ) : (
              <Box sx={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              }}>
                <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', p: 4 }}>
                  <CompareArrowsIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Upload raw and matched routes</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Compare GPS trace against road-snapped route
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default RouteMatcherVisualizer;
